import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface AppNotification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  read: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface AdminDirectMessage {
  id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
}

const PUSH_PREF_KEY = "cg_push_notifications_enabled";
const ADMIN_DM_PREFIX = "admin-dm:";

export const isPushEnabledPref = () => {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(PUSH_PREF_KEY);
  return v === null ? true : v === "true";
};

export const setPushEnabledPref = (enabled: boolean) => {
  localStorage.setItem(PUSH_PREF_KEY, enabled ? "true" : "false");
};

const normalizeNotification = (notification: AppNotification): AppNotification => ({
  ...notification,
  metadata: notification.metadata ?? {},
});

const adminDmToNotification = (message: AdminDirectMessage): AppNotification => ({
  id: `${ADMIN_DM_PREFIX}${message.id}`,
  user_id: message.recipient_id,
  type: "admin_message",
  title: "📩 Message de l'équipe Click&Gard",
  body: message.content,
  read: message.read,
  metadata: { admin_dm_id: message.id },
  created_at: message.created_at,
});

const adminDmIdFromNotification = (notification: AppNotification) => {
  const value = notification.metadata?.admin_dm_id;
  return typeof value === "string" ? value : null;
};

export const useNotificationsData = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const applyNotifications = useCallback((items: AppNotification[]) => {
    const sorted = [...items]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 30);
    setNotifications(sorted);
    setUnreadCount(sorted.filter((n) => !n.read).length);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const [notificationsResult, adminMessagesResult] = await Promise.all([
      supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30),
      supabase
        .from("admin_direct_messages")
        .select("id, recipient_id, content, read, created_at")
        .eq("recipient_id", user.id)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);

    if (notificationsResult.error) {
      console.error("notifications fetch failed", notificationsResult.error);
    }
    if (adminMessagesResult.error) {
      console.error("admin direct messages fetch failed", adminMessagesResult.error);
    }

    const regular = ((notificationsResult.data ?? []) as unknown as AppNotification[]).map(normalizeNotification);
    const mirroredAdminIds = new Set(
      regular
        .map(adminDmIdFromNotification)
        .filter((id): id is string => Boolean(id))
    );
    const adminMessages = (adminMessagesResult.data ?? []) as unknown as AdminDirectMessage[];
    const adminNotifications = adminMessages
      .filter((message) => !mirroredAdminIds.has(message.id))
      .map(adminDmToNotification);

    applyNotifications([...regular, ...adminNotifications]);
    setLoading(false);
  }, [user, applyNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    const current = notifications.find((notification) => notification.id === id);
    if (!current || current.read) return;

    const adminDmId = id.startsWith(ADMIN_DM_PREFIX)
      ? id.slice(ADMIN_DM_PREFIX.length)
      : adminDmIdFromNotification(current);

    if (adminDmId) {
      const { error } = await supabase
        .from("admin_direct_messages")
        .update({ read: true })
        .eq("id", adminDmId);
      if (error) console.error("admin direct message read update failed", error);
    }

    if (!id.startsWith(ADMIN_DM_PREFIX)) {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", id);
      if (error) console.error("notification read update failed", error);
    }

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    await Promise.all([
      supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", user.id)
        .eq("read", false),
      supabase
        .from("admin_direct_messages")
        .update({ read: true })
        .eq("recipient_id", user.id)
        .eq("read", false),
    ]);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [user]);

  const showSystemNotification = useCallback(async (title: string, body?: string) => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (!isPushEnabledPref()) return;
    try {
      // Prefer service worker registration when available (works on Android PWA)
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg && "showNotification" in reg) {
          await reg.showNotification(title, { body, icon: "/favicon.ico", badge: "/favicon.ico" });
          return;
        }
      }
      new Notification(title, { body, icon: "/favicon.ico" });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchNotifications();

    const channel = supabase
      .channel(`notifications-realtime-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const newNotif = normalizeNotification(payload.new as unknown as AppNotification);
          setNotifications((prev) => {
            const adminDmId = adminDmIdFromNotification(newNotif);
            const filtered = adminDmId
              ? prev.filter((item) => adminDmIdFromNotification(item) !== adminDmId)
              : prev;
            const next = [newNotif, ...filtered]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .slice(0, 30);
            setUnreadCount(next.filter((n) => !n.read).length);
            return next;
          });

          toast(newNotif.title, {
            description: newNotif.body ?? undefined,
            duration: newNotif.type === "admin_message" ? 8000 : 5000,
          });
          showSystemNotification(newNotif.title, newNotif.body ?? undefined);
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_direct_messages", filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const message = payload.new as unknown as AdminDirectMessage;
          const newNotif = adminDmToNotification(message);
          setNotifications((prev) => {
            const alreadyMirrored = prev.some((item) => adminDmIdFromNotification(item) === message.id);
            if (alreadyMirrored) return prev;
            const next = [newNotif, ...prev]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .slice(0, 30);
            setUnreadCount(next.filter((n) => !n.read).length);
            return next;
          });

          toast(newNotif.title, {
            description: newNotif.body ?? undefined,
            duration: 8000,
          });
          showSystemNotification(newNotif.title, newNotif.body ?? undefined);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, showSystemNotification]);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead };
};
